import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Wrench, Check, X, Clock, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

const StatusPill = ({ s }) => {
  const map = {
    pending: "bg-yellow-400/15 text-yellow-200 border-yellow-400/40",
    approved: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    rejected: "bg-red-500/10 text-red-300 border-red-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono-brand uppercase tracking-widest border ${map[s] || map.pending}`}>
      {s}
    </span>
  );
};

export default function Corrections() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [decideOpen, setDecideOpen] = useState(false);
  const [decision, setDecision] = useState({ id: null, kind: null, note: "" });

  const load = async () => {
    const { data } = await api.get("/corrections", { params: tab === "all" ? {} : { status: tab } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openDecide = (id, kind) => { setDecision({ id, kind, note: "" }); setDecideOpen(true); };
  const submitDecision = async () => {
    try {
      await api.post(`/corrections/${decision.id}/${decision.kind}`, { note: decision.note });
      toast.success(`Correction ${decision.kind}d`);
      setDecideOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Workflow"
        title="Corrections"
        subtitle="Employee requests to fix missing punch-outs"
      />

      <div className="p-8 space-y-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="corrections-tabs" className="rounded-none bg-card border border-border">
            <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-none data-[state=active]:bg-secondary">Pending</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved" className="rounded-none data-[state=active]:bg-secondary">Approved</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected" className="rounded-none data-[state=active]:bg-secondary">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all" className="rounded-none data-[state=active]:bg-secondary">All</TabsTrigger>
          </TabsList>

          <TabsContent value={tab} className="mt-4 space-y-3">
            {items.length === 0 && (
              <Card className="rounded-none border-border p-10 text-center text-muted-foreground">
                <Wrench className="h-6 w-6 mx-auto mb-2" strokeWidth={1.5} />
                No {tab === "all" ? "" : tab} correction requests.
              </Card>
            )}
            {items.map((c) => (
              <Card key={c.id} data-testid={`correction-row-${c.id}`} className="rounded-none border-border p-5">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-start">
                  <div className="space-y-2 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1.5">
                        <UserIcon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                        <span className="font-medium">{c.employee_name}</span>
                        <span className="label-mono">{c.employee_id}</span>
                      </div>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-mono-brand text-sm">{c.date}</span>
                      <StatusPill s={c.status} />
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 pt-1">
                      <div>
                        <div className="label-mono">Center</div>
                        <div className="text-sm">{c.center_name}</div>
                      </div>
                      <div>
                        <div className="label-mono">Punched in</div>
                        <div className="font-mono-brand text-sm">
                          {c.punch_in_time ? format(parseISO(c.punch_in_time), "HH:mm") : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="label-mono">Proposed out</div>
                        <div className="font-mono-brand text-sm text-[hsl(43_96%_60%)]">
                          {c.proposed_punch_out_time ? format(parseISO(c.proposed_punch_out_time), "HH:mm") : "—"}
                        </div>
                      </div>
                    </div>
                    {c.reason && (
                      <div className="pt-2">
                        <div className="label-mono">Reason from employee</div>
                        <div className="text-sm text-muted-foreground">"{c.reason}"</div>
                      </div>
                    )}
                    {c.status !== "pending" && (
                      <div className="pt-2 flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" strokeWidth={1.5} />
                        {c.status === "approved" ? "Approved" : "Rejected"} by <b className="text-foreground">{c.decided_by_name}</b>
                        {c.decided_at && ` · ${format(parseISO(c.decided_at), "d MMM HH:mm")}`}
                        {c.decision_note && <span className="italic">· "{c.decision_note}"</span>}
                      </div>
                    )}
                  </div>

                  {c.status === "pending" && (
                    <div className="flex gap-2 shrink-0">
                      <Button
                        data-testid={`reject-${c.id}`}
                        variant="outline"
                        onClick={() => openDecide(c.id, "reject")}
                        className="rounded-none border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                      >
                        <X className="h-4 w-4 mr-1" strokeWidth={2} /> Reject
                      </Button>
                      <Button
                        data-testid={`approve-${c.id}`}
                        onClick={() => openDecide(c.id, "approve")}
                        className="rounded-none btn-primary-brand"
                      >
                        <Check className="h-4 w-4 mr-1" strokeWidth={2} /> Approve
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={decideOpen} onOpenChange={setDecideOpen}>
        <DialogContent className="rounded-none bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">
              {decision.kind === "approve" ? "Approve correction" : "Reject correction"}
            </DialogTitle>
          </DialogHeader>
          <div>
            <Label className="label-mono">Note (optional)</Label>
            <Input
              data-testid="decision-note-input"
              value={decision.note}
              onChange={(e) => setDecision({ ...decision, note: e.target.value })}
              placeholder={decision.kind === "approve" ? "Confirmed via chat…" : "Reason for rejection…"}
              className="rounded-none"
            />
            <div className="label-mono mt-1">Employee will see this in their notification & email.</div>
          </div>
          <DialogFooter>
            <Button
              data-testid="submit-decision-btn"
              onClick={submitDecision}
              className={`rounded-none ${decision.kind === "approve" ? "btn-primary-brand" : "bg-red-500 hover:bg-red-400 text-red-950 font-semibold"}`}
            >
              {decision.kind === "approve" ? "Approve" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
